import React, { useState, useEffect, ChangeEvent, MouseEvent } from 'react'
import styled, { css } from 'styled-components'

import TextInput from 'components/TextInput'

import { color } from 'styles/colors'

interface AutoCompleteProps<T> {
  options?: T[]
  getOptionLabel: (option: T) => string
  getOptionKey: (option: T) => string | number
  label?: string
  initialValue?: string
  onSelected: (option: T) => void
}

const AutoComplete = <T, >({
  options = [],
  getOptionLabel,
  getOptionKey,
  label = '',
  initialValue = '',
  onSelected
}: AutoCompleteProps<T>) => {
  const [autoCompleteState, setAutoCompleteState] = useState({
    query: initialValue,
    filteredOptions: options,
    showOptions: false
  })

  useEffect(() => {
    setAutoCompleteState({ query: initialValue, filteredOptions: options, showOptions: false })
  }, [initialValue, options])

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setAutoCompleteState({
      query: value,
      filteredOptions: options.filter((option) =>
        getOptionLabel(option).toLowerCase().includes(value.toLowerCase())
      ),
      showOptions: true
    })
  }

  const onFocus = () => {
    setAutoCompleteState((prevState) => ({ ...prevState, showOptions: true }))
  }

  const onBlur = () => {
    setAutoCompleteState((prevState) => ({ ...prevState, showOptions: false }))
  }

  const onOptionSelected = (option: T) => {
    onSelected(option)
    setAutoCompleteState((prevState) => ({ ...prevState, showOptions: false }))
  }

  const { showOptions } = autoCompleteState

  return (
    <S.AutoCompleteContainer>
      <S.TextInput
        label={label}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        value={autoCompleteState.query}
      />
      <S.Icon className="icon-arrow-down2" />
      <S.DropdownOption showOptions={showOptions}>
        {autoCompleteState.filteredOptions.map((option) => {
          return (
            <S.Option
              onMouseDown={(event: MouseEvent<HTMLDivElement>) => { event.preventDefault() }}
              onClick={() => onOptionSelected(option)}
              key={getOptionKey(option)}
            >
              {getOptionLabel(option)}
            </S.Option>
          )
        })}
      </S.DropdownOption>
    </S.AutoCompleteContainer>
  )
}

export default AutoComplete

interface DropdownOptionProps {
  showOptions: boolean
}

const S = {
  AutoCompleteContainer: styled.div`
  position:relative;
  `,
  TextInput: styled(TextInput)`
  `,
  Icon: styled.i`
  position: absolute;
  right:5px;
  color: ${color('dropdown')};
  top: 38px;
  `,
  DropdownOption: styled.div.withConfig({
    shouldForwardProp: (prop) => !['showOptions'].includes(prop)
  })<DropdownOptionProps>`
    ${({ showOptions }) => showOptions
    ? css`
        display:block;
        position:absolute;
        max-height:300px;
        box-shadow: ${color('dropDownShadow')} 0 2px 7px 0;
        width:100%;
        top:64px;
        z-index:1;
        padding:5px 0px;
        overflow: auto;
        border:1px solid ${color('border')};
        background:${color('paneBackground')}`
    : css`display:none`}
  `,
  Option: styled.div`
  padding:5px 10px;
  cursor: pointer;
  &:hover {
    background:${color('backgroundHover')}
  }
  `
}
